import { Editor, useEditorState } from '@tiptap/react'
import { Button, Form, Input, Modal, Tooltip } from 'antd'
import { Link1 } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  editor: Editor
}

export const LinkButton = ({ editor }: Props) => {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)

  const [form] = Form.useForm()

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isLink: ctx.editor.isActive('link') ?? false
      }
    }
  })

  const onToggle = () => {
    setIsOpen((prev) => {
      const newState = !prev
      if (newState) {
        const previousUrl = editor.getAttributes('link').href || ''
        form.setFieldsValue({ link: previousUrl })
      }
      return newState
    })
  }

  const onLinkClick = () => {
    onToggle()
  }

  const onFinish = () => {
    const values = form.getFieldsValue()
    const url = values?.link?.trim()

    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    } else {
      editor.chain().focus().unsetLink().run()
    }

    setIsOpen(false)
    form.resetFields()
  }

  const onCancel = () => {
    setIsOpen(false)
    form.resetFields()
  }

  return (
    <>
      <Tooltip title={t('editor.link')}>
        <Button
          icon={<Link1 size={14} />}
          onClick={onLinkClick}
          type={editorState.isLink ? 'primary' : 'default'}
        />
      </Tooltip>

      <Modal
        open={isOpen}
        centered
        onCancel={onCancel}
        okButtonProps={{
          onClick: () => onFinish()
        }}
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="link"
            label={t('editor.url')}
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve()
                  }
                  try {
                    const url = value.includes(':')
                      ? new URL(value)
                      : new URL(`https://${value}`)
                    if (url.protocol === 'http:' || url.protocol === 'https:') {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error('Invalid URL'))
                  } catch {
                    return Promise.reject(new Error('Invalid URL'))
                  }
                }
              }
            ]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
